/**
 * Styremøter — the board's meetings: the call, the agenda, who came, what was decided, and the minutes as a PDF.
 * MKK module 18 «Styremøte» (C2, 2026-10-25). Staff only; nothing here is public or in the member app.
 *
 * Behind the `board_meetings` capability (the menu item is absent without it, and every route answers 404).
 *   board.read     the list, a meeting's agenda and attendance, one's own attendance, the minutes PDF, «Vedtak»;
 *   board.manage   «Nytt møte», the agenda editor (add, change, move, remove), everybody's attendance, the minutes, and
 *                  «Kall inn» / «Avhold» / «Send referat» / «Avlys» / «Slett» — each with two clicks (TwoStep).
 * The server says which in every answer (`can.manage`) and decides again on each request.
 *
 * A meeting goes draft → called → held → minutes sent, or is cancelled from draft or called. Items are numbered «n/yy»
 * by the server and stay editable until the minutes are sent; a decision is written once the meeting is held.
 * «Kall inn» marks the meeting called; no e-mail is sent from here yet.
 *
 * «Bekreftet med Vipps» (owner 2026-10-29, «connect high items only»; components/VippsConfirm): once «Send referat» froze the
 * minutes, the chair and one other board member may each confirm them with Vipps, when the mosque has switched on
 * «Bekreft styreprotokollen med Vipps». The meeting then says «Protokollen er bekreftet av …», and the PDF carries both
 * lines. The server says who gets which button (`can.confirmChair`, `can.confirmBoardMember`). Never «signatur».
 *
 * Work hub (2026-10-31):
 *   «Fast møtedag»       the board's regular day (weekly, every other week, monthly) as a planner item, and its first
 *                        meeting; «Opprett neste møte» makes the next one with the open decisions carried over.
 *   «Ny avstemning»      on an agenda item: a draft vote titled as the item, tied to it; its state and result inline (VoteCard).
 *   «Del protokoll»      the sent minutes are filed into Dokumenter («Styreprotokoller»); a sharing link that expires, as
 *                        any document's (DocumentShares, documents.share).
 */
import React, { useEffect, useState } from 'react';
import { can, download, errSummary, errText, fieldErrors, patch, post, send, type Body } from '../lib/api';
import { switchOnHint } from '../lib/features';
import { useLoad, useMsg } from '../lib/hooks';
import { Body as CardBody, Btn, Card, Field, LoadError, Loading, Msg, Tag, TwoStep } from '../components/ui';
import { FormDialog } from '../components/dialog';
import { Stamp, VoidedStamps, startConfirm, useVippsReturn } from '../components/VippsConfirm';
import { Board } from '../core';
import { useNav } from '../lib/nav';
import { VoteCard } from '../components/VoteCard';
import { DocumentShares } from '../components/DocumentShares';

const PATH = '/board';
const TONE: Record<string, 'green' | 'gold' | 'grey' | 'red'> = { draft: 'grey', called: 'gold', held: 'green', minutes_sent: 'green', cancelled: 'red' };
const two = (n: number) => String(n).padStart(2, '0');
/** An instant as the browser's wall time for <input type="datetime-local"> («2026-11-02T19:00»). */
export function toLocal(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  return `${d.getFullYear()}-${two(d.getMonth() + 1)}-${two(d.getDate())}T${two(d.getHours())}:${two(d.getMinutes())}`;
}
/** The wall time back as an instant with a zone (core refuses a wall time without one). */
export const fromLocal = (v: string): string | null => (v ? new Date(v).toISOString() : null);
const when = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleString('nb-NO', { dateStyle: 'medium', timeStyle: 'short' }) : '—');
const dayNo = (d: string | null | undefined) => (d ? d.split('-').reverse().join('.') : '—');

type MeetingForm = { title: string; startsAt: string; endsAt: string; place: string };
const EMPTY_MEETING: MeetingForm = { title: '', startsAt: '', endsAt: '', place: '' };
type ItemForm = { title: string; background: string; proposal: string; decision: string; decisionKind: string; responsibleStaffId: string; dueOn: string };
const itemForm = (i: Body): ItemForm => ({ title: i.title || '', background: i.background || '', proposal: i.proposal || '', decision: i.decision || '',
  decisionKind: i.decisionKind || '', responsibleStaffId: i.responsibleStaffId || '', dueOn: i.dueOn || '' });
/** What is sent: empty fields are cleared (null); the decision fields only when the meeting is held. */
export function itemBody(f: ItemForm, decidable: boolean): Body {
  const base: Body = { title: f.title, background: f.background || null, proposal: f.proposal || null };
  return decidable ? { ...base, decision: f.decision || null, decisionKind: f.decisionKind || null,
    responsibleStaffId: f.responsibleStaffId || null, dueOn: f.dueOn || null } : base;
}

export function Styremoter() {
  const [tab, setTab] = useState<'meetings' | 'decisions'>('meetings');
  const [open, setOpen] = useState<string | null>(null);
  const [msg, say] = useMsg();
  // Back from Vipps (components/VippsConfirm): the code is spent with this session, and the meeting opens.
  useVippsReturn('/styremoter', say, (b) => { say('Protokollen er bekreftet med Vipps.', 'ok'); setTab('meetings'); setOpen(b.subjectId); });
  return (
    <>
      <Card title="Styremøter" hint="Innkalling, saksliste, oppmøte, vedtak og referat. Bare for ansatte med tilgang; ingenting vises for medlemmer.">
        {msg.text && <CardBody><Msg text={msg.text} tone={msg.tone} /></CardBody>}
        <div className="bnr-seg shop-filter" role="group" aria-label="Styremøter: velg del">
          <button type="button" aria-pressed={tab === 'meetings'} onClick={() => setTab('meetings')}>Møter</button>
          <button type="button" aria-pressed={tab === 'decisions'} onClick={() => setTab('decisions')}>Vedtak</button>
        </div>
      </Card>
      {tab === 'decisions' ? <Decisions /> : open ? <Meeting id={open} onBack={() => setOpen(null)} /> : <MeetingList onOpen={setOpen} />}
    </>
  );
}

function MeetingFields({ f, set, errors }: { f: MeetingForm; set: (f: MeetingForm) => void; errors: Record<string, string> }) {
  const err = (k: string) => (errors[k] ? <span className="msg err" role="alert">{errors[k]}</span> : null);
  return (
    <>
      <div className="row">
        <Field label="Tittel" required className="field">{(id) => <><input id={id} maxLength={Board.MAX_TITLE} value={f.title}
          onChange={(e) => set({ ...f, title: e.target.value })} />{err('title')}</>}</Field>
      </div>
      <div className="row">
        <Field label="Starter" required className="field half">{(id) => <><input id={id} type="datetime-local" value={f.startsAt}
          onChange={(e) => set({ ...f, startsAt: e.target.value })} />{err('startsAt')}</>}</Field>
        <Field label="Slutter" className="field half">{(id) => <><input id={id} type="datetime-local" value={f.endsAt}
          onChange={(e) => set({ ...f, endsAt: e.target.value })} />{err('endsAt')}</>}</Field>
      </div>
      <div className="row">
        <Field label="Sted" className="field">{(id) => <><input id={id} maxLength={Board.MAX_PLACE} value={f.place}
          onChange={(e) => set({ ...f, place: e.target.value })} />{err('place')}</>}</Field>
      </div>
    </>
  );
}
const meetingBody = (f: MeetingForm) => ({ title: f.title, startsAt: fromLocal(f.startsAt), endsAt: fromLocal(f.endsAt), place: f.place || null });

const WEEKDAYS: [number, string][] = [[1, 'mandag'], [2, 'tirsdag'], [3, 'onsdag'], [4, 'torsdag'], [5, 'fredag'], [6, 'lørdag'], [0, 'søndag']];
const FREQ_NO: Record<string, string> = { weekly: 'Hver uke', biweekly: 'Annenhver uke', monthly: 'Én gang i måneden (samme ukedag)' };
type SeriesForm = { freq: string; weekday: string; time: string; endTime: string; place: string };
/** «Fast møtedag» → the body POST /board/series takes. */
export const seriesBody = (f: SeriesForm) => ({ freq: f.freq, weekday: Number(f.weekday), time: f.time, endTime: f.endTime || null, place: f.place || null });

/** «Fast møtedag»: the board's series, each with «Opprett neste møte»; a new one if there is none. */
function Series({ series, manage, plannerOn, onMade }: { series: Body[]; manage: boolean; plannerOn: boolean; onMade: (id: string) => void }) {
  const [msg, say] = useMsg();
  const [form, setForm] = useState<SeriesForm | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const make = async () => {
    if (!form) return;
    const r = await post(`${PATH}/series`, seriesBody(form));
    if (!r.ok) { setErrors(fieldErrors(r.body)); return say(errSummary(r.body), 'err'); }
    setForm(null); say('Den faste møtedagen er lagret, og det første møtet er laget som utkast.', 'ok'); onMade(r.body.meeting.id);
  };
  const next = async (sid: string) => {
    const r = await post(`${PATH}/series/${encodeURIComponent(sid)}/next`);
    if (!r.ok) return say(errText(r.body), 'err');
    const carried = (r.body.items || []).length;
    say(`Neste møte er laget som utkast${carried ? ` med ${carried} ${carried === 1 ? 'sak' : 'saker'} til oppfølging` : ''}.`, 'ok'); onMade(r.body.meeting.id);
  };
  if (!series.length && !manage) return null;
  return (
    <Card title="Fast møtedag" hint="Styrets faste møtedag ligger i årshjulet. Neste møte lages derfra, med vedtak som har frist fram i tid.">
      <CardBody>
        {series.map((x) => (
          <div className="row" key={x.id} style={{ alignItems: 'center' }}>
            <span><b>{x.title}</b> — {x.text}</span>
            {manage && <Btn small={false} onClick={() => next(x.id)}>Opprett neste møte</Btn>}
          </div>
        ))}
        {!series.length && manage && !plannerOn && <p className="note">En fast møtedag krever Årshjul. {switchOnHint('Årshjul', can('capabilities.manage'))}</p>}
        {!series.length && manage && plannerOn && <Btn ghost onClick={() => { setErrors({}); setForm({ freq: 'monthly', weekday: '2', time: '18:00', endTime: '20:00', place: '' }); }}>+ Sett fast møtedag</Btn>}
        <Msg text={msg.text} tone={msg.tone} />
      </CardBody>
      <FormDialog open={Boolean(form)} title="Fast møtedag" onClose={() => setForm(null)}>
        {form && <>
          <div className="row">
            <Field label="Hvor ofte" className="field half" error={errors.freq}>{(id) => <select id={id} value={form.freq} onChange={(e) => setForm({ ...form, freq: e.target.value })}>
              {Board.SERIES_FREQS.map((f) => <option key={f} value={f}>{FREQ_NO[f]}</option>)}</select>}</Field>
            <Field label="Ukedag" className="field half" error={errors.weekday}>{(id) => <select id={id} value={form.weekday} onChange={(e) => setForm({ ...form, weekday: e.target.value })}>
              {WEEKDAYS.map(([n, w]) => <option key={n} value={n}>{w}</option>)}</select>}</Field>
          </div>
          <div className="row">
            <Field label="Fra" required className="field half" error={errors.time}>{(id) => <input id={id} type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} />}</Field>
            <Field label="Til" className="field half" error={errors.endTime}>{(id) => <input id={id} type="time" value={form.endTime} onChange={(e) => setForm({ ...form, endTime: e.target.value })} />}</Field>
          </div>
          <div className="row"><Field label="Sted" className="field" error={errors.place}>{(id) => <input id={id} maxLength={Board.MAX_PLACE} value={form.place} onChange={(e) => setForm({ ...form, place: e.target.value })} />}</Field></div>
          <div className="row"><Btn small={false} disabled={!form.time} onClick={make}>Lagre og lag første møte</Btn></div>
          <Msg text={msg.text} tone={msg.tone} />
        </>}
      </FormDialog>
    </Card>
  );
}

function MeetingList({ onOpen }: { onOpen: (id: string) => void }) {
  const { data, error, reload } = useLoad<Body>(`${PATH}/meetings`);
  const [msg, say] = useMsg();
  const [form, setForm] = useState<MeetingForm | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const manage = Boolean(data && data.can && data.can.manage);
  const create = async () => {
    if (!form || saving) return;
    setSaving(true);
    try {
      const r = await post(`${PATH}/meetings`, meetingBody(form));
      if (!r.ok) { setErrors(fieldErrors(r.body)); return say(errSummary(r.body), 'err'); }
      setForm(null); reload(); onOpen(r.body.meeting.id);
    } finally { setSaving(false); }
  };
  const rows = (list: Body[], empty: string) => (
    <table className="t">
      <thead><tr><th scope="col">Møte</th><th scope="col">Tid</th><th scope="col">Status</th><th scope="col">Saker</th><th scope="col">Mitt oppmøte</th></tr></thead>
      <tbody>{list.length ? list.map((m) => (
        <tr key={m.id}>
          <td><a href="#" onClick={(e) => { e.preventDefault(); onOpen(m.id); }}><b>{m.title}</b></a>{m.place && <div className="sub">{m.place}</div>}</td>
          <td>{when(m.startsAt)}{(data!.overdue || []).includes(m.id) && <> <Tag tone="gold">Ikke avholdt</Tag></>}</td>
          <td><Tag tone={TONE[m.status] || 'grey'}>{Board.STATUS_NO[m.status] || m.status}</Tag></td>
          <td>{m.itemCount}</td>
          <td>{m.myAttendance ? Board.ATTENDANCE_NO[m.myAttendance] : '—'}</td>
        </tr>)) : <tr><td colSpan={5}>{empty}</td></tr>}</tbody>
    </table>
  );
  return (
    <>
    {data && <Series series={data.series || []} manage={manage} plannerOn={Boolean(data.can && data.can.plannerOn)} onMade={(mid) => { reload(); onOpen(mid); }} />}
    <Card title="Møter">
      {manage && <div className="work-bar"><Btn small={false} onClick={() => { setErrors({}); say(''); setForm(EMPTY_MEETING); }}>+ Nytt møte</Btn></div>}
      {!form && <CardBody><Msg text={msg.text} tone={msg.tone} /></CardBody>}
      {error ? <LoadError error={error} retry={reload} /> : !data ? <Loading /> : (
        <>
          <CardBody><h3>Kommende</h3></CardBody>
          {rows(data.upcoming || [], manage ? 'Ingen møter planlagt. Lag det første med «Nytt møte».' : 'Ingen møter planlagt.')}
          <CardBody><h3>Avholdte og avlyste</h3></CardBody>
          {rows(data.held || [], 'Ingen ennå.')}
        </>
      )}
      <FormDialog open={Boolean(form)} title="Nytt styremøte" dirty={Boolean(form) && JSON.stringify(form) !== JSON.stringify(EMPTY_MEETING)} onClose={() => setForm(null)}>
        {form && <>
          <MeetingFields f={form} set={setForm} errors={errors} />
          <div className="row"><Btn small={false} disabled={saving} onClick={create}>{saving ? 'Lagrer …' : 'Lag utkast'}</Btn></div>
          <Msg text={msg.text} tone={msg.tone} />
        </>}
      </FormDialog>
    </Card>
    </>
  );
}

function Meeting({ id, onBack }: { id: string; onBack: () => void }) {
  const at = `${PATH}/meetings/${encodeURIComponent(id)}`;
  const { data, error, reload } = useLoad<Body>(at, [id]);
  const [msg, say] = useMsg();
  const [edit, setEdit] = useState<MeetingForm | null>(null);
  const [item, setItem] = useState<{ id: string | null; f: ItemForm } | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [newTitle, setNewTitle] = useState('');
  const [minutes, setMinutes] = useState<string | null>(null);
  const [going, setGoing] = useState(false);
  const [sharing, setSharing] = useState(false);
  const { go } = useNav();
  useEffect(() => { setMinutes(null); setSharing(false); }, [id]);

  if (error) return <Card title="Styremøte"><LoadError error={error} retry={reload} /></Card>;
  if (!data) return <Card title="Styremøte"><Loading /></Card>;
  const m = data.meeting, items: Body[] = data.items || [], manage = Boolean(data.can && data.can.manage);
  const editable = Boolean(data.editable), decidable = Boolean(data.decidable), next: string[] = data.next || [];
  const staffName = (sid: string | null) => (data.board || []).find((p: Body) => p.id === sid)?.name || '';
  const done = (r: { ok: boolean; body: Body }, ok: string) => { if (r.ok) { say(ok, 'ok'); setErrors({}); reload(); return true; } setErrors(fieldErrors(r.body)); say(errSummary(r.body), 'err'); return false; };
  const step = async (what: string, ok: string) => { done(await post(`${at}/${what}`), ok); };
  const saveMeeting = async () => { if (edit && done(await patch(at, meetingBody(edit)), 'Møtet er lagret.')) setEdit(null); };
  const saveItem = async () => {
    if (!item) return;
    const r = item.id ? await patch(`${at}/items/${item.id}`, itemBody(item.f, decidable)) : await post(`${at}/items`, itemBody(item.f, decidable));
    if (done(r, 'Saken er lagret.')) setItem(null);
  };
  const addItem = async () => { if (newTitle.trim() && done(await post(`${at}/items`, { title: newTitle }), 'Saken er lagt til.')) setNewTitle(''); };
  const move = async (i: number, by: number) => {
    const ids = items.map((x) => x.id); const j = i + by; if (j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j], ids[i]];
    done(await send(`${at}/order`, { method: 'PUT', body: JSON.stringify({ ids }) }), 'Rekkefølgen er lagret.');
  };
  const attend = async (sid: string, status: string, note: string | null) => {
    const own = sid === data.me.id && !manage;
    done(await send(own ? `${at}/my-attendance` : `${at}/attendance/${sid}`, { method: 'PUT', body: JSON.stringify({ status, note: note || null }) }), 'Oppmøtet er lagret.');
  };
  const saveMinutes = async () => { if (minutes !== null && done(await send(`${at}/minutes`, { method: 'PUT', body: JSON.stringify({ minutesText: minutes }) }), 'Referatet er lagret.')) setMinutes(null); };
  const pdf = async () => {
    const r = await download(`/v1/admin${at}/minutes.pdf`, `referat-styremote-${(m.startsAt || '').slice(0, 10)}.pdf`);
    if (!r.ok) say(r.message || 'Kunne ikke laste ned referatet.', 'err');
  };
  const del = async () => { const r = await send(at, { method: 'DELETE' }); if (r.ok) onBack(); else say(r.body.message || errText(r.body), 'err'); };
  // «Ny avstemning» on an agenda item: a draft titled as the item, tied to it; it is opened and run in Avstemninger.
  const newVote = async (it: Body) => {
    const r = await post('/voting/votes', { title: it.title, boardMeetingItemId: it.id });
    if (!r.ok) return say(errText(r.body), 'err');
    say(`Utkast til avstemning om sak ${it.number} er laget. Åpne det i Avstemninger.`, 'ok'); reload();
  };
  const votes: Record<string, Body[]> = data.votes || {};
  const att = new Map((data.attendance || []).map((a: Body) => [a.staffUserId, a]));
  const conf: Body = data.confirmations || {};
  const confirmVipps = async (role: 'chair' | 'board_member') => {
    if (going) return;
    setGoing(true);
    const e = await startConfirm('board_minutes', m.id, role);
    if (e) { say(e, 'err'); setGoing(false); }
  };
  const showConfirm = m.status === 'minutes_sent' && (data.confirmationOffered || conf.chair || conf.board_member);
  const minutesText = minutes ?? (m.minutesText || '');

  return (
    <>
      <Card title={m.title} hint={`${when(m.startsAt)}${m.endsAt ? ' – ' + when(m.endsAt) : ''}${m.place ? ' · ' + m.place : ''}`}>
        <div className="work-bar">
          <Btn ghost onClick={onBack}>← Alle møter</Btn>
          <Tag tone={TONE[m.status] || 'grey'}>{Board.STATUS_NO[m.status] || m.status}</Tag>
          {m.calledAt && <span className="note">Innkalt {when(m.calledAt)}</span>}
        </div>
        {manage && <div className="work-bar">
          {editable && <Btn ghost onClick={() => { setErrors({}); setEdit({ title: m.title, startsAt: toLocal(m.startsAt), endsAt: toLocal(m.endsAt), place: m.place || '' }); }}>Endre møtet</Btn>}
          {next.includes('called') && <TwoStep label="Kall inn" ghost={false} confirmLabel="Klikk igjen: møtet markeres som innkalt" onConfirm={() => step('call', 'Møtet er innkalt.')} />}
          {next.includes('held') && <TwoStep label="Avhold" ghost={false} confirmLabel="Klikk igjen: møtet er avholdt, vedtak kan føres" onConfirm={() => step('hold', 'Møtet er avholdt.')} />}
          {next.includes('minutes_sent') && <TwoStep label="Send referat" ghost={false} confirmLabel="Klikk igjen: referatet sendes, og møtet låses" onConfirm={() => step('send-minutes', 'Referatet er sendt. Møtet er låst.')} />}
          {next.includes('cancelled') && <TwoStep label="Avlys" confirmLabel="Klikk igjen: møtet avlyses" onConfirm={() => step('cancel', 'Møtet er avlyst.')} />}
          {Board.deletable(m.status) && <TwoStep label="Slett utkastet" confirmLabel="Klikk igjen: utkastet og sakene slettes" onConfirm={del} />}
        </div>}
        {['held', 'minutes_sent'].includes(m.status) && <div className="work-bar">
          <Btn small={false} onClick={pdf}>Lag referat (PDF)</Btn>
          {data.minutesDocument && data.can.share && <Btn ghost onClick={() => setSharing(true)}>Del protokoll</Btn>}
          {data.minutesDocument && <span className="note">Arkivert i Dokumenter (Styreprotokoller).</span>}
          <span className="note">PDF-en skrives med latinske bokstaver; arabisk og urdu vises som «?».</span>
        </div>}
        {m.status === 'called' && <CardBody><p className="note">Innkallingen er registrert. Ingen e-post sendes herfra ennå.</p></CardBody>}
        {showConfirm && <CardBody>
          <h4>Protokollen bekreftet med Vipps</h4>
          {data.confirmedText ? <p><Tag tone="green">Bekreftet</Tag> {data.confirmedText}</p> : <p className="note">Styrelederen og et annet styremedlem kan bekrefte protokollen med Vipps, hver for seg.</p>}
          <table className="t"><tbody>
            <Stamp label="Styreleder" s={conf.chair || null} approver changedText="Bekreftelsen gjelder ikke lenger — protokollen er ikke den samme." />
            <Stamp label="Styremedlem" s={conf.board_member || null} approver changedText="Bekreftelsen gjelder ikke lenger — protokollen er ikke den samme." />
          </tbody></table>
          <VoidedStamps voided={conf.voided} changedWord="endret" />
          {(data.can.confirmChair || data.can.confirmBoardMember) && <div className="row">
            {data.can.confirmChair && <Btn small={false} disabled={going} onClick={() => confirmVipps('chair')}>{going ? 'Åpner Vipps …' : 'Bekreft protokollen med Vipps (styreleder)'}</Btn>}
            {data.can.confirmBoardMember && <Btn small={false} disabled={going} onClick={() => confirmVipps('board_member')}>{going ? 'Åpner Vipps …' : 'Bekreft protokollen med Vipps (styremedlem)'}</Btn>}
            <span className="note"> Du bekrefter at protokollen over er riktig. Ingenting i møtet endres.</span>
          </div>}
        </CardBody>}
        <CardBody><Msg text={msg.text} tone={msg.tone} /></CardBody>
      </Card>

      <Card title="Saksliste" hint={decidable ? 'Møtet er avholdt: før vedtak, type, ansvarlig og frist for hver sak.' : 'Sakene nummereres «nr/år» av systemet. Vedtak føres når møtet er avholdt.'}>
        <table className="t">
          <thead><tr><th scope="col">Nr.</th><th scope="col">Sak</th><th scope="col">Vedtak</th><th scope="col">Ansvarlig / frist</th>
            <th scope="col"><span className="vh">Handling</span></th></tr></thead>
          <tbody>{items.length ? items.map((it, i) => (
            <tr key={it.id}>
              <td>{it.number}</td>
              <td><b>{it.title}</b>{it.proposal && <div className="sub">Forslag: {it.proposal}</div>}
                {(votes[it.id] || []).map((v) => <VoteCard key={v.id} vote={v} compact onOpen={() => go('voting')} />)}</td>
              <td>{it.decisionKind && <Tag tone={it.decisionKind === 'decided' ? 'green' : it.decisionKind === 'rejected' ? 'red' : 'grey'}>{Board.KIND_NO[it.decisionKind]}</Tag>}
                {it.decision && <div className="sub" style={{ whiteSpace: 'pre-line' }}>{it.decision}</div>}</td>
              <td>{it.responsibleName || staffName(it.responsibleStaffId) || '—'}{it.dueOn && <div className="sub">Frist {dayNo(it.dueOn)}</div>}</td>
              <td>{manage && editable && <>
                <Btn ghost aria-label={`Flytt sak ${it.number} opp`} disabled={i === 0} onClick={() => move(i, -1)}>↑</Btn>{' '}
                <Btn ghost aria-label={`Flytt sak ${it.number} ned`} disabled={i === items.length - 1} onClick={() => move(i, 1)}>↓</Btn>{' '}
                <Btn ghost aria-label={`Endre sak ${it.number}`} onClick={() => { setErrors({}); setItem({ id: it.id, f: itemForm(it) }); }}>{decidable ? 'Vedtak' : 'Endre'}</Btn>{' '}
                <TwoStep label="Fjern" ariaLabel={`Fjern sak ${it.number}`} confirmLabel="Klikk igjen: saken fjernes"
                  onConfirm={async () => { done(await send(`${at}/items/${it.id}`, { method: 'DELETE' }), 'Saken er fjernet.'); }} />
              </>}
                {data.can.vote && !(votes[it.id] || []).some((v) => ['draft', 'open'].includes(v.status)) && <>{' '}
                  <Btn ghost aria-label={`Ny avstemning om sak ${it.number}`} onClick={() => newVote(it)}>Ny avstemning</Btn></>}</td>
            </tr>)) : <tr><td colSpan={5}>Ingen saker ennå.</td></tr>}</tbody>
        </table>
        {manage && editable && <div className="work-bar">
          <Field label="Ny sak" className="field work-search">{(fid) => <input id={fid} maxLength={Board.MAX_ITEM_TITLE} value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') void addItem(); }} placeholder="Tittel på saken" />}</Field>
          <Btn small={false} disabled={!newTitle.trim()} onClick={addItem}>Legg til sak</Btn>
        </div>}
      </Card>

      <Card title="Oppmøte" hint="Styrets medlemmer: ansatte med tilgang til styremøter.">
        <table className="t">
          <thead><tr><th scope="col">Navn</th><th scope="col">Oppmøte</th><th scope="col">Merknad</th></tr></thead>
          <tbody>{(data.board || []).map((p: Body) => {
            const a = att.get(p.id) as Body | undefined;
            const mine = p.id === data.me.id, may = editable && (manage || mine);
            return (
              <tr key={p.id}>
                <td>{p.name}{mine && <span className="sub"> (deg)</span>}</td>
                <td>{may
                  ? <select aria-label={`Oppmøte for ${p.name}`} value={a ? a.status : ''} onChange={(e) => e.target.value && attend(p.id, e.target.value, a ? a.note : null)}>
                      <option value="">—</option>
                      {Board.ATTENDANCE.map((s) => <option key={s} value={s}>{Board.ATTENDANCE_NO[s]}</option>)}
                    </select>
                  : a ? Board.ATTENDANCE_NO[a.status] : '—'}</td>
                <td>{may && a
                  ? <input aria-label={`Merknad for ${p.name}`} maxLength={Board.MAX_NOTE} defaultValue={a.note || ''}
                      onBlur={(e) => { if ((e.target.value || null) !== (a.note || null)) attend(p.id, a.status, e.target.value); }} />
                  : (a && a.note) || ''}</td>
              </tr>);
          })}</tbody>
        </table>
      </Card>

      <Card title="Referat" hint="Skriv bare det saken trenger om personer. Teksten kommer med i PDF-en.">
        <CardBody>
          {manage && editable
            ? <>
                <Field label="Referat" className="field">{(fid) => <textarea id={fid} rows={10} maxLength={Board.MAX_MINUTES} value={minutesText}
                  onChange={(e) => setMinutes(e.target.value)} />}</Field>
                <Btn small={false} disabled={minutes === null} onClick={saveMinutes}>Lagre referat</Btn>
              </>
            : <p style={{ whiteSpace: 'pre-line' }}>{m.minutesText || 'Ingen referattekst.'}</p>}
        </CardBody>
      </Card>

      <FormDialog open={sharing && Boolean(data.minutesDocument)} title="Del protokoll" wide onClose={() => setSharing(false)}>
        {sharing && data.minutesDocument && <DocumentShares documentId={data.minutesDocument.id} label="Protokoll" />}
      </FormDialog>
      <FormDialog open={Boolean(edit)} title="Endre møtet" onClose={() => setEdit(null)}>
        {edit && <>
          <MeetingFields f={edit} set={setEdit} errors={errors} />
          <div className="row"><Btn small={false} onClick={saveMeeting}>Lagre</Btn></div>
          <Msg text={msg.text} tone={msg.tone} />
        </>}
      </FormDialog>
      <FormDialog open={Boolean(item)} title={decidable ? 'Sak og vedtak' : 'Endre sak'} onClose={() => setItem(null)}>
        {item && <ItemFields f={item.f} set={(f) => setItem({ ...item, f })} errors={errors} decidable={decidable} board={data.board || []} onSave={saveItem} msg={msg} />}
      </FormDialog>
    </>
  );
}

function ItemFields({ f, set, errors, decidable, board, onSave, msg }: {
  f: ItemForm; set: (f: ItemForm) => void; errors: Record<string, string>; decidable: boolean; board: Body[]; onSave: () => void; msg: { text: string; tone: any };
}) {
  const err = (k: string) => (errors[k] ? <span className="msg err" role="alert">{errors[k]}</span> : null);
  return (
    <>
      <div className="row"><Field label="Tittel" required className="field">{(id) => <><input id={id} maxLength={Board.MAX_ITEM_TITLE} value={f.title}
        onChange={(e) => set({ ...f, title: e.target.value })} />{err('title')}</>}</Field></div>
      <div className="row"><Field label="Bakgrunn" className="field">{(id) => <><textarea id={id} rows={4} maxLength={Board.MAX_BACKGROUND} value={f.background}
        onChange={(e) => set({ ...f, background: e.target.value })} />{err('background')}</>}</Field></div>
      <div className="row"><Field label="Forslag til vedtak" className="field">{(id) => <><textarea id={id} rows={3} maxLength={Board.MAX_PROPOSAL} value={f.proposal}
        onChange={(e) => set({ ...f, proposal: e.target.value })} />{err('proposal')}</>}</Field></div>
      {decidable && <>
        <div className="row">
          <Field label="Vedtakstype" className="field half">{(id) => <><select id={id} value={f.decisionKind} onChange={(e) => set({ ...f, decisionKind: e.target.value })}>
            <option value="">—</option>{Board.DECISION_KINDS.map((k) => <option key={k} value={k}>{Board.KIND_NO[k]}</option>)}</select>{err('decisionKind')}</>}</Field>
          <Field label="Ansvarlig" className="field half">{(id) => <><select id={id} value={f.responsibleStaffId} onChange={(e) => set({ ...f, responsibleStaffId: e.target.value })}>
            <option value="">—</option>{board.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</select>{err('responsibleStaffId')}</>}</Field>
        </div>
        <div className="row"><Field label="Vedtak" className="field">{(id) => <><textarea id={id} rows={3} maxLength={Board.MAX_DECISION} value={f.decision}
          onChange={(e) => set({ ...f, decision: e.target.value })} />{err('decision')}</>}</Field></div>
        <div className="row"><Field label="Frist" className="field half">{(id) => <><input id={id} type="date" value={f.dueOn}
          onChange={(e) => set({ ...f, dueOn: e.target.value })} />{err('dueOn')}</>}</Field></div>
      </>}
      <div className="row"><Btn small={false} disabled={!f.title.trim()} onClick={onSave}>Lagre</Btn></div>
      <Msg text={msg.text} tone={msg.tone} />
    </>
  );
}

/** «Vedtak»: the decision register — every item that ended «decided», newest meeting first; search and a year. */
function Decisions() {
  const [typed, setTyped] = useState('');
  const [q, setQ] = useState('');
  const [year, setYear] = useState('');
  useEffect(() => { const t = setTimeout(() => setQ(typed.trim()), 250); return () => clearTimeout(t); }, [typed]);
  const qs = new URLSearchParams({ ...(q ? { q } : {}), ...(/^\d{4}$/.test(year) ? { year } : {}) }).toString();
  const { data, error, reload } = useLoad<Body>(`${PATH}/decisions${qs ? '?' + qs : ''}`, [qs]);
  const items: Body[] = (data && data.items) || [];
  return (
    <Card title="Vedtak" hint="Vedtaksregisteret: alle saker som endte med «Vedtatt».">
      <div className="work-bar">
        <Field label="Søk" className="field work-search">{(id) => <input id={id} type="search" value={typed} maxLength={120}
          onChange={(e) => setTyped(e.target.value)} placeholder="Nummer, tittel eller vedtak" />}</Field>
        <Field label="År" className="field">{(id) => <input id={id} inputMode="numeric" maxLength={4} value={year} onChange={(e) => setYear(e.target.value)} placeholder="ÅÅÅÅ" />}</Field>
      </div>
      {error ? <LoadError error={error} retry={reload} /> : !data ? <Loading /> : (
        <table className="t">
          <thead><tr><th scope="col">Nr.</th><th scope="col">Sak</th><th scope="col">Vedtak</th><th scope="col">Ansvarlig / frist</th><th scope="col">Møte</th></tr></thead>
          <tbody>{items.length ? items.map((d) => (
            <tr key={d.id}>
              <td>{d.number}</td><td><b>{d.title}</b></td>
              <td style={{ whiteSpace: 'pre-line' }}>{d.decision || '—'}</td>
              <td>{d.responsibleName || '—'}{d.dueOn && <div className="sub">Frist {dayNo(d.dueOn)}</div>}</td>
              <td>{d.meetingTitle}<div className="sub">{dayNo(d.meetingStartsOn)}</div></td>
            </tr>)) : <tr><td colSpan={5}>{q || year ? 'Ingen vedtak passer søket.' : 'Ingen vedtak ennå.'}</td></tr>}</tbody>
        </table>
      )}
    </Card>
  );
}
